import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Globe,
    Search,
    Plus,
    RefreshCw,
    Trash2,
    ArrowLeft,
    Database,
    ShieldCheck,
    Key,
    ExternalLink,
    Layout
} from 'lucide-react';
import { LysepAPI } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';

const DNSManagement = () => {
    const { id: serverId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Manually extract zoneName from path
    const getZoneFromPath = () => {
        const parts = location.pathname.split('/');
        const mgmtIndex = parts.indexOf('management');
        if (mgmtIndex !== -1 && parts[mgmtIndex + 1]) {
            return parts[mgmtIndex + 1];
        }
        return null;
    };

    const zoneName = getZoneFromPath();

    const [zones, setZones] = useState([]);
    const [records, setRecords] = useState([]);
    const [loadingZones, setLoadingZones] = useState(true);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal states
    const [isAddingZone, setIsAddingZone] = useState(false);
    const [deletingZone, setDeletingZone] = useState(null);
    const [newZoneName, setNewZoneName] = useState('');
    const [isAddingRecord, setIsAddingRecord] = useState(false);

    // New Record Form State
    const [newRecord, setNewRecord] = useState({
        domainName: '',
        type: 'A',
        ipAddress: '',
        cname: '',
        exchange: '',
        preference: 10,
        text: '',
        ttl: 3600
    });

    const fetchZones = async () => {
        setLoadingZones(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/dns/zones`);
            if (resp.data.state) {
                const zData = resp.data.data;
                setZones(Array.isArray(zData) ? zData : (zData?.zones || []));
            }
        } catch (err) {
            console.error(err);
            toast.error("Zone listesi alınamadı");
        } finally {
            setLoadingZones(false);
        }
    };

    const fetchRecords = async (name) => {
        if (!name) return;
        setLoadingRecords(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/dns/zones/${name}/records`);
            if (resp.data.state) {
                const rData = resp.data.data;
                setRecords(Array.isArray(rData) ? rData : (rData?.records || []));
            }
        } catch (err) {
            console.error(err);
            toast.error("Kayıtlar alınamadı");
        } finally {
            setLoadingRecords(false);
        }
    };

    useEffect(() => {
        fetchZones();
    }, [serverId]);

    useEffect(() => {
        if (zoneName) {
            fetchRecords(zoneName);
        }
    }, [serverId, zoneName]);

    const handleZoneClick = (name) => {
        navigate(`/panel/servers/${serverId}/dns/management/${name}/records`);
    };

    const handleAddZone = async (e) => {
        e.preventDefault();
        try {
            const resp = await LysepAPI.post(`/api/servers/${serverId}/dns/zones`, {
                zoneName: newZoneName,
                type: 'primary'
            });
            if (resp.data.state) {
                toast.success("Zone eklendi");
                setNewZoneName('');
                setIsAddingZone(false);
                fetchZones();
                navigate(`/panel/servers/${serverId}/dns/management/${newZoneName}/records`);
            } else {
                toast.error(resp.data.message || "Zone eklenemedi");
            }
        } catch (err) {
            toast.error("Zone eklenemedi: " + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteZone = async () => {
        if (!deletingZone) return;
        try {
            const resp = await LysepAPI.delete(`/api/servers/${serverId}/dns/zones/${deletingZone}`);
            if (resp.data.state) {
                toast.success("Zone başarıyla silindi");
                setDeletingZone(null);
                fetchZones();
                navigate(`/panel/servers/${serverId}/dns/management`);
            }
        } catch (err) {
            toast.error("Zone silinemedi: " + (err.response?.data?.message || err.message));
        }
    };

    const handleAddRecord = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                domain: newRecord.domainName ? (newRecord.domainName === '@' ? zoneName : `${newRecord.domainName}.${zoneName}`) : zoneName,
                type: newRecord.type,
                ttl: newRecord.ttl,
            };

            if (newRecord.type === 'A' || newRecord.type === 'AAAA') payload.ipAddress = newRecord.ipAddress;
            if (newRecord.type === 'CNAME') payload.cname = newRecord.cname;
            if (newRecord.type === 'MX') {
                payload.exchange = newRecord.exchange;
                payload.preference = newRecord.preference;
            }
            if (newRecord.type === 'TXT') payload.text = newRecord.text;

            const resp = await LysepAPI.post(`/api/servers/${serverId}/dns/zones/${zoneName}/records`, payload);
            if (resp.data.state) {
                toast.success("Kayıt eklendi");
                setIsAddingRecord(false);
                setNewRecord({ ...newRecord, domainName: '', ipAddress: '', cname: '', exchange: '', text: '' });
                fetchRecords(zoneName);
            } else {
                toast.error(resp.data.message || "Kayıt eklenemedi");
            }
        } catch (err) {
            toast.error("Kayıt eklenemedi: " + (err.response?.data?.message || err.message));
        }
    };

    const [deletingRecord, setDeletingRecord] = useState(null);

    const handleDeleteRecord = async () => {
        if (!deletingRecord) return;
        try {
            // Technitium needs the RData to uniquely identify a record for deletion
            const payload = {
                domain: deletingRecord.name,
                type: deletingRecord.type,
                ...deletingRecord.rData
            };

            console.log('Sending delete request with payload:', payload);

            const resp = await LysepAPI.delete(`/api/servers/${serverId}/dns/zones/${zoneName}/records`, {
                data: payload
            });
            if (resp.data.state) {
                toast.success("Kayıt silindi");
                setDeletingRecord(null);
                fetchRecords(zoneName);
            }
        } catch (err) {
            console.error('Delete error:', err);
            toast.error("Kayıt silinemedi: " + (err.response?.data?.message || err.message));
        }
    };

    const formatRData = (type, rData) => {
        if (!rData) return '-';
        switch (type) {
            case 'A': return rData.ipAddress;
            case 'AAAA': return rData.ipAddress;
            case 'CNAME': return rData.cname;
            case 'MX': return `${rData.preference} ${rData.exchange}`;
            case 'TXT': return rData.text;
            case 'PTR': return rData.ptrName;
            case 'NS': return rData.nameServer;
            case 'SRV': return `${rData.priority} ${rData.weight} ${rData.port} ${rData.target}`;
            case 'SOA': return `${rData.primaryNameServer} ${rData.responsiblePerson}`;
            default: return JSON.stringify(rData);
        }
    };

    const filteredZones = (zones || []).filter(z =>
        z.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="fixed inset-0 z-[60] bg-[#0a0f14] flex flex-col animate-fadeIn">
            {/* Full Screen Header */}
            <div className="flex-none px-6 py-4 border-b border-white/10 bg-[#0f141a] flex items-center justify-between shadow-2xl">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/panel/servers/${serverId}/dns`)}
                        className="group flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white bg-white/5 border border-white/10 rounded-xl transition-all hover:bg-white/10"
                    >
                        <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                        <span className="text-sm font-semibold">Geri Dön</span>
                    </button>
                    <div className="h-6 w-px bg-white/10 mx-2" />
                    <div>
                        <h1 className="text-lg font-bold text-white flex items-center gap-2">
                            <Globe className="w-4 h-4 text-blue-400" />
                            Gelişmiş DNS Management
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    <div className="flex flex-col items-end">
                        <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Sunucu</span>
                        <span className="text-sm text-gray-300 font-mono italic">#{serverId}</span>
                    </div>
                    {zoneName && (
                        <div className="flex flex-col items-end px-6 border-l border-white/10">
                            <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Aktif Zone</span>
                            <span className="text-sm text-blue-400 font-bold">{zoneName}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Zones Sidebar */}
                <div className="w-80 flex-none border-r border-white/10 bg-[#0f1115] flex flex-col">
                    <div className="p-4 border-b border-white/10 bg-black/20">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Zones</h3>
                            <button
                                onClick={() => setIsAddingZone(true)}
                                className="p-1 px-2 flex items-center gap-1 text-[10px] font-black bg-blue-600/20 text-blue-400 border border-blue-500/20 rounded-md hover:bg-blue-600/40 transition-all font-sans"
                            >
                                <Plus className="w-3 h-3" />
                                YENİ ZONE
                            </button>
                        </div>
                        <div className="relative group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 group-focus-within:text-blue-500" />
                            <input
                                type="text"
                                placeholder="Zone ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-black/30 border border-white/5 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-blue-500/50 transition-all font-mono"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
                        {loadingZones ? (
                            <div className="p-10 text-center">
                                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500/50" />
                            </div>
                        ) : (
                            filteredZones.map(zone => (
                                <button
                                    key={zone.name}
                                    onClick={() => handleZoneClick(zone.name)}
                                    className={`w-full flex flex-col items-start gap-1 p-4 rounded-xl transition-all border ${zoneName === zone.name
                                        ? 'bg-blue-500/10 border-blue-500/20 text-blue-400 shadow-lg shadow-blue-500/5'
                                        : 'border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <span className={`font-bold text-sm truncate ${zoneName === zone.name ? 'text-blue-400' : ''}`}>{zone.name}</span>
                                        <span className="text-[9px] opacity-60 font-black uppercase tracking-tighter">{zone.type}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className={`w-1.5 h-1.5 rounded-full ${zone.disabled ? 'bg-red-500' : 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'}`} />
                                        <span className="text-[10px] opacity-50 font-medium">{zone.disabled ? 'Pasif' : 'Aktif'}</span>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Records Content Container */}
                <div className="flex-1 flex flex-col min-w-0 bg-[#0a0e13]">
                    {!zoneName ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-10 animate-fadeIn">
                            <div className="p-8 bg-white/[0.02] rounded-full mb-6 border border-white/5">
                                <Layout className="w-20 h-20 opacity-10" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Yönetim Başlatın</h2>
                            <p className="max-w-xs text-center text-sm leading-relaxed opacity-60 font-medium">DNS kayıtlarını görüntülemek, eklemek veya silmek için soldaki listeden bir zone seçin.</p>
                        </div>
                    ) : (
                        <>
                            {/* Records Header */}
                            <div className="px-8 py-6 border-b border-white/10 flex items-center justify-between bg-black/10">
                                <div>
                                    <div className="flex items-center gap-4 mb-1">
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-2xl font-black text-white">{zoneName}</h2>
                                            <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded text-[10px] font-black border border-blue-500/20 tracking-widest uppercase font-sans">
                                                Records
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => setDeletingZone(zoneName)}
                                            className="p-1.5 text-gray-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                                            title="Zone'u Sil"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold">Kayıt Listesi ve Detaylar</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => fetchRecords(zoneName)}
                                        className="p-3 text-gray-400 hover:text-white bg-white/5 border border-white/10 rounded-xl transition-all hover:rotate-180 duration-500"
                                    >
                                        <RefreshCw className={`w-5 h-5 ${loadingRecords ? 'animate-spin' : ''}`} />
                                    </button>
                                    <button
                                        onClick={() => setIsAddingRecord(true)}
                                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-black rounded-xl transition-all shadow-xl shadow-blue-500/20 hover:scale-105 active:scale-95 uppercase tracking-wider font-sans"
                                    >
                                        <Plus className="w-5 h-5" />
                                        Kayıt Ekle
                                    </button>
                                </div>
                            </div>

                            {/* Records Table */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar px-8">
                                {loadingRecords ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                        <RefreshCw className="w-10 h-10 animate-spin mb-4 text-blue-500" />
                                        <p className="font-bold uppercase tracking-widest text-xs">Kayıtlar Hazırlanıyor...</p>
                                    </div>
                                ) : (
                                    <table className="w-full text-left text-sm mt-6 mb-10">
                                        <thead className="sticky top-0 bg-[#0a0e13] z-10 shadow-sm font-sans">
                                            <tr className="text-gray-500 font-black uppercase tracking-[0.2em] text-[10px] border-b border-white/5">
                                                <th className="px-6 py-4 font-sans">İsim / Host</th>
                                                <th className="px-6 py-4 font-sans">Tür</th>
                                                <th className="px-6 py-4 font-sans">Veri / Hedef</th>
                                                <th className="px-6 py-4 font-sans">TTL</th>
                                                <th className="px-6 py-4 text-right font-sans">İşlemler</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            {(records || []).map((record, idx) => (
                                                <tr key={idx} className="hover:bg-white/[0.03] transition-all group border-b border-white/5">
                                                    <td className="px-6 py-5 font-mono text-gray-300 relative group-hover:text-blue-400">
                                                        {record.name}
                                                    </td>
                                                    <td className="px-6 py-5 font-sans">
                                                        <span className="px-2.5 py-1 bg-white/5 text-gray-300 rounded text-[10px] font-black border border-white/10 uppercase group-hover:border-blue-500/30 group-hover:text-blue-400 transition-colors font-sans">
                                                            {record.type}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5 font-mono text-gray-400 break-all text-xs group-hover:text-gray-200">
                                                        {formatRData(record.type, record.rData)}
                                                    </td>
                                                    <td className="px-6 py-5 text-gray-500 text-[11px] font-bold font-sans">
                                                        {record.ttlString || `${record.ttl}s`}
                                                    </td>
                                                    <td className="px-6 py-5 text-right">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeletingRecord(record);
                                                            }}
                                                            className="p-2.5 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all rounded-xl hover:bg-red-500/10"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}

                                            {(!records || records.length === 0) && (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-20 text-center text-gray-500 italic">
                                                        <div className="flex flex-col items-center justify-center opacity-30">
                                                            <Database className="w-16 h-16 mb-4" />
                                                            <p className="font-bold uppercase tracking-widest text-xs font-sans">Bu zone içerisinde kayıt bulunamadı.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Modals */}
            {isAddingZone && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 shadow-2xl w-full max-w-md animate-scaleUp">
                        <h3 className="text-xl font-bold text-white mb-6 font-sans">Yeni Zone Ekle</h3>
                        <form onSubmit={handleAddZone} className="space-y-6">
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Zone Adı (Domain)</label>
                                <input
                                    type="text"
                                    autoFocus
                                    required
                                    placeholder="example.com"
                                    value={newZoneName}
                                    onChange={(e) => setNewZoneName(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setIsAddingZone(false)} className="flex-1 px-4 py-3 bg-[#252525] hover:bg-[#303030] text-gray-300 font-bold rounded-xl transition-all font-sans">İptal</button>
                                <button type="submit" className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-500/20 font-sans">Zone Ekle</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isAddingRecord && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 shadow-2xl w-full max-w-2xl animate-scaleUp overflow-y-auto max-h-[90vh] custom-scrollbar">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-xl font-bold text-white font-sans">Yeni Kayıt Ekle</h3>
                                <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mt-1 font-sans">{zoneName}</p>
                            </div>
                            <button onClick={() => setIsAddingRecord(false)} className="text-gray-500 hover:text-white transition-colors">
                                <Plus className="w-6 h-6 rotate-45" />
                            </button>
                        </div>

                        <form onSubmit={handleAddRecord} className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Kayıt Tipi</label>
                                    <select
                                        value={newRecord.type}
                                        onChange={(e) => setNewRecord({ ...newRecord, type: e.target.value })}
                                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none cursor-pointer font-sans"
                                    >
                                        <option value="A" className="bg-[#1a1a1a]">A (IPv4)</option>
                                        <option value="AAAA" className="bg-[#1a1a1a]">AAAA (IPv6)</option>
                                        <option value="CNAME" className="bg-[#1a1a1a]">CNAME</option>
                                        <option value="MX" className="bg-[#1a1a1a]">MX</option>
                                        <option value="TXT" className="bg-[#1a1a1a]">TXT</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">TTL (Saniye)</label>
                                    <input
                                        type="number"
                                        value={newRecord.ttl}
                                        onChange={(e) => setNewRecord({ ...newRecord, ttl: e.target.value })}
                                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-sans"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Host / Domain Adı</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="@ veya subdomain"
                                        value={newRecord.domainName}
                                        onChange={(e) => setNewRecord({ ...newRecord, domainName: e.target.value })}
                                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 text-sm font-mono italic pointer-events-none">
                                        {newRecord.domainName ? (newRecord.domainName === '@' ? zoneName : `${newRecord.domainName}.${zoneName}`) : zoneName}
                                    </span>
                                </div>
                            </div>

                            {/* Dynamic Fields */}
                            {(newRecord.type === 'A' || newRecord.type === 'AAAA') && (
                                <div className="animate-slideDown">
                                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">IP Adresi</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder={newRecord.type === 'A' ? '1.2.3.4' : '2001:db8::1'}
                                        value={newRecord.ipAddress}
                                        onChange={(e) => setNewRecord({ ...newRecord, ipAddress: e.target.value })}
                                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono"
                                    />
                                </div>
                            )}

                            {newRecord.type === 'CNAME' && (
                                <div className="animate-slideDown">
                                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Hedef Domain (Target)</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="example.com"
                                        value={newRecord.cname}
                                        onChange={(e) => setNewRecord({ ...newRecord, cname: e.target.value })}
                                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono"
                                    />
                                </div>
                            )}

                            {newRecord.type === 'MX' && (
                                <div className="grid grid-cols-3 gap-6 animate-slideDown">
                                    <div className="col-span-2">
                                        <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Mail Server</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="mail.example.com"
                                            value={newRecord.exchange}
                                            onChange={(e) => setNewRecord({ ...newRecord, exchange: e.target.value })}
                                            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Öncelik</label>
                                        <input
                                            type="number"
                                            required
                                            value={newRecord.preference}
                                            onChange={(e) => setNewRecord({ ...newRecord, preference: e.target.value })}
                                            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-sans"
                                        />
                                    </div>
                                </div>
                            )}

                            {newRecord.type === 'TXT' && (
                                <div className="animate-slideDown">
                                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 font-sans">Metin (TXT Data)</label>
                                    <textarea
                                        required
                                        rows="3"
                                        value={newRecord.text}
                                        onChange={(e) => setNewRecord({ ...newRecord, text: e.target.value })}
                                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono"
                                    />
                                </div>
                            )}

                            <div className="flex gap-3 pt-6 font-sans">
                                <button type="button" onClick={() => setIsAddingRecord(false)} className="flex-1 px-4 py-3 bg-[#252525] hover:bg-[#303030] text-gray-300 font-bold rounded-xl transition-all font-sans">İptal</button>
                                <button type="submit" className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-500/20 font-sans">Kayıt Ekle</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deletingRecord && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-fadeIn">
                    <div className="bg-[#1a1a1a] border border-red-500/30 rounded-2xl p-8 shadow-2xl w-full max-w-md animate-scaleUp text-center">
                        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20">
                            <Trash2 className="w-10 h-10 text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2 font-sans">Kaydı Sil?</h3>
                        <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                            <span className="font-mono text-blue-400">{deletingRecord.name}</span> ({deletingRecord.type}) kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeletingRecord(null)}
                                className="flex-1 px-4 py-3 bg-[#252525] hover:bg-[#303030] text-gray-300 font-bold rounded-xl transition-all font-sans"
                            >
                                Vazgeç
                            </button>
                            <button
                                onClick={handleDeleteRecord}
                                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-red-500/20 font-sans"
                            >
                                Evet, Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {deletingZone && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-fadeIn">
                    <div className="bg-[#1a1a1a] border border-red-500/50 rounded-2xl p-10 shadow-[0_0_50px_rgba(239,68,68,0.2)] w-full max-w-lg animate-scaleUp text-center">
                        <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-8 border border-red-500/40 shadow-inner">
                            <Trash2 className="w-12 h-12 text-red-500 animate-pulse" />
                        </div>
                        <h3 className="text-2xl font-extrabold text-white mb-4 font-sans uppercase tracking-tight">Zone'u Kalıcı Olarak Sil?</h3>
                        <p className="text-gray-400 text-base mb-10 leading-relaxed font-medium">
                            <span className="text-red-400 font-bold font-mono px-2 py-1 bg-red-500/10 rounded border border-red-500/20">{deletingZone}</span> alanı ve içindeki <span className="text-white font-bold">tüm DNS kayıtları</span> kalıcı olarak silinecektir. Bu işlemin geri dönüşü yoktur.
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setDeletingZone(null)}
                                className="flex-1 px-6 py-4 bg-[#252525] hover:bg-[#303030] text-gray-300 font-black rounded-xl transition-all font-sans uppercase tracking-widest text-xs"
                            >
                                Vazgeç
                            </button>
                            <button
                                onClick={handleDeleteZone}
                                className="flex-1 px-6 py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl transition-all shadow-xl shadow-red-500/40 font-sans uppercase tracking-widest text-xs"
                            >
                                Tehlike: Onayla ve Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DNSManagement;
